import { useCallback, useMemo } from "react";

import { Event } from "@tsmono/inspect-common";
import { kDefaultExcludeEvents } from "@tsmono/inspect-components/transcript";

import { useStore } from "../../../state/store";

// TODO: span_begin/end are not in AllEventTypes. Is that on purpose?
export type AllEventTypes = Exclude<Event["event"], "span_begin" | "span_end">;

const eventTypes: Record<AllEventTypes, string> = {
  sample_init: "Sample Init",
  sample_limit: "Sample Limit",
  sandbox: "Sandbox",
  state: "State",
  store: "Store",
  model: "Model",
  tool: "Tool",
  approval: "Approval",
  review: "Review",
  sentinel: "Sentinel",
  input: "Input",
  interrupt: "Interrupt",
  score: "Score",
  score_edit: "Score Edit",
  error: "Error",
  logger: "Logger",
  compaction: "Compaction",
  info: "Info",
  subtask: "Subtask",
  anchor: "Anchor",
  branch: "Branch",
  checkpoint: "Checkpoint",
  step: "Step",
} as const;

export const useTranscriptFilter = (
  defaultExcludeEvents: readonly string[] = kDefaultExcludeEvents
) => {
  const stored = useStore((state) => state.sample.eventFilter.filteredTypes);
  const filtered = useMemo(
    () => stored ?? [...defaultExcludeEvents],
    [stored, defaultExcludeEvents]
  );
  const setFilteredEventTypes = useStore(
    (state) => state.sampleActions.setFilteredEventTypes
  );

  const filterEventType = useCallback(
    (type: AllEventTypes, isFiltered: boolean) => {
      const newFiltered = new Set(filtered);
      if (isFiltered) {
        newFiltered.delete(type);
      } else {
        newFiltered.add(type);
      }
      setFilteredEventTypes(Array.from(newFiltered));
    },
    [filtered, setFilteredEventTypes]
  );

  const setDebugFilter = useCallback(() => {
    setFilteredEventTypes([]);
  }, [setFilteredEventTypes]);

  const setDefaultFilter = useCallback(() => {
    // null = dynamic default (resolved per sample)
    setFilteredEventTypes(null);
  }, [setFilteredEventTypes]);

  const setNoneFilter = useCallback(() => {
    setFilteredEventTypes(Object.keys(eventTypes));
  }, [setFilteredEventTypes]);

  const isDefaultFilter = useMemo(() => {
    return stored === null;
  }, [stored]);

  const isDebugFilter = useMemo(() => {
    return filtered.length === 0;
  }, [filtered]);

  const isNoneFilter = useMemo(() => {
    const allTypes = Object.keys(eventTypes);
    return (
      filtered.length === allTypes.length &&
      allTypes.every((type) => filtered.includes(type))
    );
  }, [filtered]);

  const arrangedEventTypes = useCallback(
    (columns: number = 1) => {
      // Object.keys widens to string[]; eventTypes is a module-local literal
      // declared Record<AllEventTypes, string>, so its keys are exactly that.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Object.keys boundary
      const keys = Object.keys(eventTypes) as AllEventTypes[];

      // Sort keys alphabetically with default disabled keys at the end
      const sortedKeys = keys.sort((a, b) => {
        const aIsDefault = defaultExcludeEvents.includes(a);
        const bIsDefault = defaultExcludeEvents.includes(b);

        // If one is in default exclude set and the other isn't, default goes to end
        if (aIsDefault && !bIsDefault) return 1;
        if (!aIsDefault && bIsDefault) return -1;

        // Both are in same category (both default or both not default), sort alphabetically
        return eventTypes[a].localeCompare(eventTypes[b]);
      });

      if (columns === 1) {
        return sortedKeys;
      }

      // Arrange for multi-column layout with proper reading order
      const itemsPerColumn = Math.ceil(sortedKeys.length / columns);
      const columnArrays: AllEventTypes[][] = [];

      // Split into columns
      for (let col = 0; col < columns; col++) {
        const start = col * itemsPerColumn;
        const end = Math.min(start + itemsPerColumn, sortedKeys.length);
        columnArrays.push(sortedKeys.slice(start, end));
      }

      // Interleave items from all columns
      const arrangedKeys: AllEventTypes[] = [];
      const maxItemsInColumn = Math.max(
        ...columnArrays.map((col) => col.length)
      );

      for (let row = 0; row < maxItemsInColumn; row++) {
        for (let col = 0; col < columns; col++) {
          const item = columnArrays[col]?.[row];
          if (item !== undefined) {
            arrangedKeys.push(item);
          }
        }
      }

      return arrangedKeys;
    },
    [defaultExcludeEvents]
  );

  return {
    filtered,
    isDefaultFilter,
    isDebugFilter,
    isNoneFilter,
    setDefaultFilter,
    setDebugFilter,
    setNoneFilter,
    filterEventType,
    eventTypes,
    arrangedEventTypes,
  };
};
